import MockAdapter from "axios-mock-adapter";
import { VacancyDto } from "@/modules/recruitment/dtos/VacancyDto";
import { LocationType } from "@/modules/recruitment/enums/LocationType";
import { JobType } from "@/modules/recruitment/enums/JobType";
import { VacancyStatus } from "@/modules/recruitment/enums/VacancyStatus";
import { SeniorityLevel } from "@/modules/recruitment/enums/SeniorityLevel";
import { Department } from "@/modules/recruitment/enums/Department";

// Mock Vacancies Data
export const mockVacancies: VacancyDto[] = [
  {
    id: "job-1",
    organizationId: "org-1",
    title: "Senior Frontend Developer",
    description:
      "Lead the development of our customer-facing web applications and mentor junior developers. Responsible for designing and implementing scalable frontend architectures.",
    department: Department.Engineering,
    locationType: LocationType.Hybrid,
    jobType: JobType.Contract,
    status: VacancyStatus.Active,
    minimumSalary: 80000,
    maximumSalary: 120000,
    baseScore: 60,
    applicationDeadline: new Date(
      Date.now() + 30 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    yearsOfExperience: 0,
    applicationCount: 2,
    keyResponsibilities: [
      { name: "Lead frontend architecture design", weight: 9 },
      { name: "Mentor junior developers", weight: 7 },
      { name: "Code reviews and quality assurance", weight: 8 },
    ],
    requiredSkills: [
      { name: "React", weight: 9 },
      { name: "TypeScript", weight: 8 },
      { name: "CSS", weight: 7 },
      { name: "Git", weight: 6 },
    ],
    optionalSkills: [
      { name: "Node.js", weight: 5 },
      { name: "GraphQL", weight: 4 },
      { name: "Testing frameworks", weight: 5 },
    ],
    educationLevel: "Bachelor's degree in Computer Science or equivalent",
    seniorityLevel: SeniorityLevel.Junior,
    certifications: [],
  },
  {
    id: "job-2",
    organizationId: "org-1",
    title: "DevOps Engineer",
    description:
      "Maintain and improve our CI/CD pipelines and cloud infrastructure. Manage AWS/Azure infrastructure and ensure security best practices.",
    department: Department.IT,
    locationType: LocationType.Remote,
    jobType: JobType.Permanent,
    status: VacancyStatus.Active,
    minimumSalary: 70000,
    maximumSalary: 100000,
    baseScore: 60,
    applicationDeadline: new Date(
      Date.now() + 45 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    yearsOfExperience: 0,
    applicationCount: 1,
    keyResponsibilities: [
      { name: "Manage CI/CD pipelines", weight: 9 },
      { name: "Cloud infrastructure maintenance", weight: 8 },
      { name: "Security and monitoring", weight: 7 },
    ],
    requiredSkills: [
      { name: "AWS/Azure", weight: 9 },
      { name: "Docker", weight: 8 },
      { name: "Kubernetes", weight: 8 },
      { name: "Terraform", weight: 7 },
    ],
    optionalSkills: [
      { name: "Python", weight: 5 },
      { name: "Ansible", weight: 4 },
      { name: "Monitoring tools", weight: 5 },
    ],
    educationLevel: "Bachelor's degree in Computer Science or equivalent",
    certifications: [],
    seniorityLevel: SeniorityLevel.Junior,
  },
  {
    id: "job-3",
    organizationId: "org-1",
    title: "Product Manager",
    description:
      "Define product strategy and roadmap for our HR management platform. Coordinate with engineering and design teams to deliver exceptional products.",
    department: Department.Management,
    locationType: LocationType.OnSite,
    jobType: JobType.Permanent,
    status: VacancyStatus.Expired,
    minimumSalary: 90000,
    maximumSalary: 130000,
    applicationDeadline: new Date(
      Date.now() - 5 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    baseScore: 60,
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    yearsOfExperience: 0,
    applicationCount: 1,
    keyResponsibilities: [
      { name: "Define product roadmap", weight: 9 },
      { name: "Stakeholder management", weight: 8 },
      { name: "Feature prioritization", weight: 8 },
    ],
    requiredSkills: [
      { name: "Product strategy", weight: 9 },
      { name: "Agile methodologies", weight: 8 },
      { name: "Data analysis", weight: 7 },
      { name: "Communication", weight: 8 },
    ],
    optionalSkills: [
      { name: "SQL", weight: 5 },
      { name: "UX design", weight: 4 },
      { name: "Market research", weight: 5 },
    ],
    educationLevel:
      "Bachelor's degree in Business, Computer Science or related field",
    certifications: [],
    seniorityLevel: SeniorityLevel.Junior,
  },
];

export const registerVacancyMocks = (mock: MockAdapter) => {
  // Get all vacancies
  mock.onGet(/\/api\/organizations\/([^/]+)\/vacancies$/).reply((config) => {
    const searchTerm = config.params?.searchTerm?.toLowerCase()

    if (searchTerm) {
      const filtered = mockVacancies.filter(
        (v) =>
          v.title.toLowerCase().includes(searchTerm) ||
          v.department.toLowerCase().includes(searchTerm) ||
          v.description.toLowerCase().includes(searchTerm),
      )
      return [200, filtered]
    }

    return [200, mockVacancies]
  })

  // Get vacancies by status
  mock.onGet(/\/api\/vacancy\/status\/([^/]+)$/).reply((config) => {
    const status = config.url?.split("/").pop() as VacancyStatus
    const filtered = mockVacancies.filter((v) => v.status === status)
    return [200, filtered]
  })

  // Get vacancy by ID
  mock.onGet(/\/api\/vacancy\/([^/]+)$/).reply((config) => {
    const id = config.url?.split("/").pop()
    const vacancy = mockVacancies.find((v) => v.id === id)

    if (vacancy) {
      return [200, vacancy]
    }
    return [404, { message: "Vacancy not found" }]
  })

  // Create vacancy
  mock.onPost("/api/vacancy").reply((config) => {
    const vacancyData = JSON.parse(config.data ?? "{}")
    const newVacancy: VacancyDto = {
      id: `job-${Date.now()}`,
      organizationId: "org-1",
      ...vacancyData,
      status: VacancyStatus.Active,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      applicationCount: 0,
    }

    mockVacancies.push(newVacancy)
    return [201, newVacancy]
  })

  // Update vacancy
  mock.onPut(/\/api\/vacancy\/([^/]+)$/).reply((config) => {
    const id = config.url?.split("/").pop()?.split("?")[0]
    const updates = JSON.parse(config.data ?? "{}")

    const vacancyIndex = mockVacancies.findIndex((v) => v.id === id)
    if (vacancyIndex !== -1) {
      mockVacancies[vacancyIndex] = {
        ...mockVacancies[vacancyIndex],
        ...updates,
        updatedAt: new Date().toISOString(),
      }
      return [200, mockVacancies[vacancyIndex]]
    }
    return [404, { message: "Vacancy not found" }]
  })

  // Delete vacancy
  mock.onDelete(/\/api\/vacancy\/([^/]+)$/).reply((config) => {
    const id = config.url?.split("/").pop()
    const vacancyIndex = mockVacancies.findIndex((v) => v.id === id)

    if (vacancyIndex !== -1) {
      mockVacancies.splice(vacancyIndex, 1)
      return [204]
    }
    return [404, { message: "Vacancy not found" }]
  })

  // AI Generate Vacancy
  mock.onPost("/ai/generate-vacancy").reply((config) => {
    JSON.parse(config.data ?? "{}")

    // Simulate AI generation with a delay
    return new Promise((resolve) => {
      setTimeout(() => {
        const aiGeneratedVacancy = {
          title: "Senior Full Stack Developer",
          description:
            "We are seeking a talented Senior Full Stack Developer to join our growing engineering team. You will be responsible for designing, developing, and maintaining scalable web applications using modern technologies. This role requires strong technical skills, excellent problem-solving abilities, and the ability to work collaboratively in an agile environment.",
          seniorityLevel: SeniorityLevel.Senior,
          keyResponsibilities: [
            {
              name: "Design and develop full-stack web applications using React, Node.js, and PostgreSQL",
              weight: 9,
            },
            {
              name: "Write clean, maintainable, and well-documented code",
              weight: 8,
            },
            {
              name: "Collaborate with product managers and designers to implement new features",
              weight: 7,
            },
            {
              name: "Participate in code reviews and mentor junior developers",
              weight: 7,
            },
            {
              name: "Optimize application performance and scalability",
              weight: 8,
            },
            {
              name: "Contribute to architectural decisions and technical planning",
              weight: 6,
            },
          ],
          requiredSkills: [
            { name: "React", weight: 9 },
            { name: "TypeScript", weight: 9 },
            { name: "Node.js", weight: 8 },
            { name: "PostgreSQL", weight: 7 },
            { name: "RESTful APIs", weight: 8 },
            { name: "Git", weight: 6 },
            { name: "Agile/Scrum methodologies", weight: 5 },
          ],
          optionalSkills: [
            { name: "GraphQL", weight: 5 },
            { name: "AWS", weight: 5 },
            { name: "Docker", weight: 4 },
            { name: "Kubernetes", weight: 4 },
            { name: "CI/CD", weight: 4 },
            { name: "Testing frameworks (Jest, Cypress)", weight: 5 },
          ],
          certifications: [
            { name: "AWS Certified Solutions Architect", weight: 6 },
            { name: "Certified Kubernetes Administrator", weight: 5 },
          ],
          yearsOfExperience: 5,
          educationLevel:
            "Bachelor's degree in Computer Science or equivalent experience",
          locationType: LocationType.Hybrid,
          jobType: JobType.Permanent,
          minimumSalary: 90000,
          maximumSalary: 140000,
        }

        resolve([200, aiGeneratedVacancy])
      }, 2000)
    })
  })

  // Sage 300 vacancies list
  mock.onGet(/\/api\/organizations\/([^/]+)\/sage300\/vacancies$/).reply(200, [
    {
      positionId: 1,
      positionCode: "POS001",
      description: "Senior Developer",
      positionStatus: "Open",
      companyCode: "001",
      companyRuleCode: "IMAGINE",
    },
    {
      positionId: 2,
      positionCode: "POS002",
      description: "HR Manager",
      positionStatus: "Open",
      companyCode: "001",
      companyRuleCode: "IMAGINE",
    },
    {
      positionId: 3,
      positionCode: "POS003",
      description: "Accountant",
      positionStatus: "Open",
      companyCode: "001",
      companyRuleCode: "IMAGINE",
    },
  ])

  // Sage 300 generate vacancy from position
  mock
    .onPost(
      /\/api\/organizations\/([^/]+)\/sage300\/vacancies\/(\d+)\/generate-vacancy$/,
    )
    .reply((config) => {
      const positionId = parseInt(config.url?.split("/").slice(-2, -1)[0] ?? "1")
      const positionMap: Record<number, { positionCode: string; description: string }> = {
        1: { positionCode: "POS001", description: "Senior Developer" },
        2: { positionCode: "POS002", description: "HR Manager" },
        3: { positionCode: "POS003", description: "Accountant" },
      }
      const position = positionMap[positionId] ?? { positionCode: "POS001", description: "Senior Developer" }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve([
            200,
            {
              title: position.description,
              description: `We are seeking a talented ${position.description} to join our team.`,
              seniorityLevel: SeniorityLevel.Senior,
              keyResponsibilities: [
                { name: "Lead technical delivery and code quality", weight: 9 },
                { name: "Collaborate with cross-functional teams", weight: 7 },
                { name: "Mentor junior team members", weight: 6 },
              ],
              requiredSkills: [
                { name: "Relevant technical expertise", weight: 9 },
                { name: "Problem-solving", weight: 8 },
              ],
              optionalSkills: [{ name: "Project management", weight: 5 }],
              certifications: [],
              yearsOfExperience: 5,
              educationLevel: "Bachelor's degree or equivalent",
              locationType: LocationType.Hybrid,
              jobType: JobType.Permanent,
              minimumSalary: 80000,
              maximumSalary: 120000,
              sageMetadata: {
                companyCode: "001",
                companyRuleCode: "IMAGINE",
                positionCode: position.positionCode,
                positionId: positionId,
                rawPayload: null,
              },
            },
          ])
        }, 1500)
      })
    })

  // Sage 300 submit employee
  mock
    .onPost(
      /\/api\/organizations\/([^/]+)\/sage300\/submit\/([^/]+)$/,
    )
    .reply(200, {
      success: true,
      message: "Employee submitted to Sage 300 successfully.",
      entityCode: "MOCK_ENT_001",
    })

  // Sage 300 submission status
  mock
    .onGet(
      /\/api\/organizations\/([^/]+)\/sage300\/submission\/([^/]+)\/status$/,
    )
    .reply(200, {
      employeeId: "00000000-0000-0000-0000-000000000001",
      entityCode: "MOCK_ENT_001",
      status: 2, // EmployeeCreated
      lastErrorPayload: null,
      updatedAt: new Date().toISOString(),
    })

  return mock
};
